import { useState } from "react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Reusable confirmation dialog for irreversible, high-consequence
 * actions (permanent deletes, cascades that touch many users, etc.).
 *
 * Wraps the shadcn <AlertDialog> primitive with:
 *   - A destructive-styled trigger you provide via `children`.
 *   - Async-aware confirm button that spins + disables during the
 *     `onConfirm` promise, then auto-closes on success.
 *   - Warning icon + description slot for the "what will happen" copy.
 *   - Uniform `data-testid` naming — pass `testId` and get
 *     `<testId>-confirm-dialog`, `<testId>-confirm`, `<testId>-cancel`.
 *
 * Why not just use browser `confirm()`? Native confirm dialogs are
 * unstyled, block the JS thread, don't respect the theme, and users
 * frequently misread them on mobile because the OS renders them in
 * the wrong colour scheme. AlertDialog also lets us describe the
 * *consequences* — critical for an operation-of-record app where
 * mistakes cost real horses and real money.
 */
export function ConfirmDialog({
  children,               // trigger element (e.g. a <Button> with a Trash2 icon)
  title,                  // headline like "Delete Fireball?"
  description,            // JSX or string describing the consequences
  confirmLabel = "Yes, do it",
  cancelLabel = "Cancel",
  destructive = true,     // switches confirm button styling
  onConfirm,              // async () => void
  testId = "confirm",     // used for data-testid namespacing
  onOpenChange,           // optional callback so parents can track state
}) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const handleOpenChange = (v) => {
    if (busy) return;             // don't allow closing while the action is in flight
    setOpen(v);
    onOpenChange?.(v);
  };
  const handleConfirm = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await onConfirm?.();
      setOpen(false);
      onOpenChange?.(false);
    } catch (err) {
      // Callers should show their own toast, but we catch here so the
      // dialog doesn't stay in a broken half-busy state on error.
      // eslint-disable-next-line no-console
      console.error("[ConfirmDialog] onConfirm rejected:", err);
      toast.error(err?.response?.data?.detail || err?.message || "Action failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <AlertDialog open={open} onOpenChange={handleOpenChange}>
      <AlertDialogTrigger asChild>{children}</AlertDialogTrigger>
      <AlertDialogContent data-testid={`${testId}-confirm-dialog`}>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            {destructive && (
              <AlertTriangle className="h-5 w-5 text-[hsl(var(--destructive))]" aria-hidden />
            )}
            {title}
          </AlertDialogTitle>
          {description && (
            <AlertDialogDescription asChild>
              <div className="text-sm text-muted-foreground">{description}</div>
            </AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy} data-testid={`${testId}-cancel`}>
            {cancelLabel}
          </AlertDialogCancel>
          <AlertDialogAction
            onClick={handleConfirm}
            disabled={busy}
            className={destructive
              ? "bg-[hsl(var(--destructive))] hover:bg-[hsl(var(--destructive))]/90 focus:ring-[hsl(var(--destructive))]"
              : undefined}
            data-testid={`${testId}-confirm`}
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
            {confirmLabel}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
